import {
  CameraFilled,
  CheckCircleFilled,
  DownloadOutlined,
  ExclamationCircleFilled,
  FilePdfOutlined,
  LeftOutlined,
  RightOutlined,
  WhatsAppOutlined,
} from '@ant-design/icons';
import {
  BillDetails,
  billsCount,
  countByStatus,
  cubicMeters,
  describeError,
  dirhams,
  HIGH_CONSUMPTION,
  latestPerBill,
  type MonthKey,
  metersCount,
  monthEnd,
  monthLabel,
  monthStart,
  present,
  readingsStatus,
  toCents,
  WHATSAPP_ERRORS,
  WHATSAPP_STATUS_COLORS,
  WHATSAPP_STATUS_LABELS,
  type WhatsappStatus,
  withDownload,
} from '@horof/shared';
import {
  Alert,
  App,
  Button,
  DatePicker,
  Modal,
  Segmented,
  Skeleton,
  Table,
  Tag,
} from 'antd';
import dayjs from 'dayjs';
import { type ReactNode, useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router';
import { useMutation, useQuery } from 'urql';
import {
  closeContext,
  DeleteBillDocument,
  LatePayersDocument,
  type MonthBillRow,
  MonthCloseDocument,
  MonthWhatsappDocument,
  ResendInvoiceDocument,
  SendMonthInvoicesDocument,
} from './close-data';
import { nextMonth, parseMonth, previousMonth } from './dates';
import { useFitColumns } from './table-fit';
import { PageHeader } from './ui';
import { useIsCommittee } from './water-data';

type StepState = 'done' | 'warn' | 'todo';
type BillView = 'all' | 'high' | 'zero' | 'unpaid' | 'duplicates';

/**
 * «إغلاق الشهر»: the readings, the month's bills to review (high and zero
 * consumptions, duplicates), sending them by WhatsApp, printing, and what is
 * shared in the village groups. The phone keeps a read-only status.
 */
export function CloseScreen() {
  const canEdit = useIsCommittee();
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const month = parseMonth(params.get('m'));
  const setMonth = (m: MonthKey) => setParams({ m }, { replace: true });
  const variables = useMemo(
    () => ({ start: monthStart(month), end: monthEnd(month), month }),
    [month],
  );
  const [{ data, error }] = useQuery({
    query: MonthCloseDocument,
    variables,
    context: useMemo(() => closeContext, []),
  });
  const [view, setView] = useState<BillView>('all');
  const [bill, setBill] = useState<string | null>(null);

  const readings = useMemo(
    () => readingsStatus(data?.meters?.nodes ?? []),
    [data],
  );
  const billed = data?.report?.nodes[0];
  const report = billed && billed.billsCount > 0 ? billed : null;
  const bills = useMemo(
    () =>
      present(data?.bills?.nodes).sort(
        (a, b) => (a.meter?.sortOrder ?? 0) - (b.meter?.sortOrder ?? 0),
      ),
    [data],
  );
  const duplicateMeters = new Set(readings.duplicates.map((m) => m.id));
  // The newer bills of a duplicated meter (the first one is kept).
  const deletable = new Set(
    readings.duplicates.flatMap((m) =>
      m.bills
        .slice(1)
        .flatMap((b) => (toCents(b.paidAmount ?? '0') === 0 ? [b.id] : [])),
    ),
  );
  const views: Record<BillView, MonthBillRow[]> = {
    all: bills,
    high: bills.filter((b) => (b.consumption ?? 0) > HIGH_CONSUMPTION),
    zero: bills.filter((b) => b.consumption === 0),
    unpaid: bills.filter((b) => b.billStatus !== 'PAID'),
    duplicates: bills.filter((b) => b.meter && duplicateMeters.has(b.meter.id)),
  };
  const read = readings.total - readings.unread.length;
  const states: StepState[] = [
    readings.unread.length ? 'warn' : 'done',
    readings.duplicates.length ? 'warn' : report ? 'done' : 'todo',
    'todo',
    'todo',
    'todo',
  ];

  return (
    <section className="desk-page desk-summary">
      <PageHeader
        title="إغلاق الشهر"
        description="خطوات الشهر: القراءات، الفواتير، الإرسال عبر واتساب، النشر."
        actions={
          <span className="desk-month-picker">
            <Button
              icon={<RightOutlined />}
              title="الشهر السابق"
              aria-label="الشهر السابق"
              onClick={() => setMonth(previousMonth(month))}
            />
            <DatePicker
              picker="month"
              allowClear={false}
              format="MMMM YYYY"
              value={dayjs(`${month}-01`)}
              onChange={(d) => d && setMonth(d.format('YYYY-MM') as MonthKey)}
            />
            <Button
              icon={<LeftOutlined />}
              title="الشهر التالي"
              aria-label="الشهر التالي"
              onClick={() => setMonth(nextMonth(month))}
            />
          </span>
        }
      />
      {error && <Alert type="error" showIcon message="تعذّر تحميل الشهر" />}
      {!data && !error && <Skeleton active paragraph={{ rows: 10 }} />}

      {data && (
        <div className="desk-summary-body">
          <div className="desk-close-steps">
            {['القراءات', 'مراجعة الفواتير', 'واتساب', 'الطباعة', 'النشر'].map(
              (title, i) => (
                <a
                  key={title}
                  href={`#step-${i + 1}`}
                  className={`is-${states[i]}`}
                >
                  <span className="desk-step-number">{i + 1}</span>
                  {title}
                  {states[i] === 'done' && <CheckCircleFilled />}
                  {states[i] === 'warn' && <ExclamationCircleFilled />}
                </a>
              ),
            )}
          </div>

          <div className="desk-summary-grid">
            <Card n={1} title="القراءات" state={states[0]}>
              <div className="desk-big-count">
                <span className="ltr-num">
                  {read} / {readings.total}
                </span>{' '}
                <span className="field-hint">
                  {readings.unread.length
                    ? `${metersCount(readings.unread.length)} بدون قراءة`
                    : 'كل العدادات مقروءة'}
                </span>
              </div>
              {readings.unread.map((m) => (
                <button
                  key={m.id}
                  type="button"
                  className="desk-line desk-line-button"
                  onClick={() => navigate(`/meters/${m.id}`)}
                >
                  <span>{m.memberName}</span>
                  <span className="field-hint">العداد {m.label}</span>
                </button>
              ))}
            </Card>

            <Card n={3} title="إرسال الفواتير عبر واتساب" state="todo">
              <WhatsappCard
                month={month}
                start={variables.start}
                end={variables.end}
                unreadCount={readings.unread.length}
                duplicateCount={readings.duplicates.length}
                hasBills={report !== null}
                canSend={canEdit}
              />
            </Card>

            <Card n={2} title="مراجعة الفواتير" state={states[1]} wide>
              {report ? (
                <div className="desk-close-report">
                  <strong>{billsCount(report.billsCount)}</strong> ·{' '}
                  {cubicMeters(report.consumption)} ·{' '}
                  <strong>{dirhams(report.billedTotal)}</strong>
                  {data.billsLink && (
                    <Button
                      size="small"
                      icon={<FilePdfOutlined />}
                      href={data.billsLink}
                      target="_blank"
                    >
                      فواتير الشهر (PDF)
                    </Button>
                  )}
                </div>
              ) : (
                <div className="field-hint">لا توجد فواتير لهذا الشهر بعد.</div>
              )}
              {readings.duplicates.length > 0 && (
                <Alert
                  type="warning"
                  showIcon
                  message={`${metersCount(readings.duplicates.length)} بأكثر من فاتورة هذا الشهر: سيتوصل العضو بالاثنتين. احذف الأحدث إن لم يُؤدَّ عليها شيء.`}
                />
              )}
              <Segmented<BillView>
                value={view}
                onChange={setView}
                options={[
                  { value: 'all', label: `الكل (${views.all.length})` },
                  {
                    value: 'high',
                    label: `أكثر من ${HIGH_CONSUMPTION} م³ (${views.high.length})`,
                  },
                  { value: 'zero', label: `منعدم (${views.zero.length})` },
                  {
                    value: 'unpaid',
                    label: `غير مؤداة (${views.unpaid.length})`,
                  },
                  {
                    value: 'duplicates',
                    label: `مكررة (${views.duplicates.length})`,
                  },
                ]}
              />
              <BillsTable
                rows={views[view]}
                deletable={canEdit ? deletable : new Set()}
                onOpen={setBill}
              />
            </Card>

            <Card n={4} title="الطباعة" state="todo">
              <div className="field-hint">
                لمن لا تصله رسائل واتساب، ولمن يؤدي في المكتب. ملفات PDF بترتيب
                الجولة، صالحة 7 أيام.
              </div>
              <div className="desk-link-list">
                <a href={data.billsLink} target="_blank" rel="noreferrer">
                  <FilePdfOutlined /> فواتير الشهر
                </a>
                <a href={data.receiptsLink} target="_blank" rel="noreferrer">
                  <FilePdfOutlined /> وصولات الشهر
                </a>
                <a
                  href={data.unreachableNoticesLink}
                  target="_blank"
                  rel="noreferrer"
                >
                  <FilePdfOutlined /> إشعارات الأداء (بدون هاتف)
                </a>
                <a href={data.allNoticesLink} target="_blank" rel="noreferrer">
                  <FilePdfOutlined /> كل إشعارات الأداء
                </a>
              </div>
            </Card>

            <Card n={5} title="النشر في مجموعات واتساب" state="todo">
              <SharingCard month={month} bulletinLink={data.bulletinLink} />
            </Card>
          </div>
        </div>
      )}
      {bill && (
        <Modal
          open
          width={560}
          footer={null}
          title="تفاصيل الفاتورة"
          onCancel={() => setBill(null)}
        >
          <BillDetails billId={bill} memberLink={(id) => `/members/${id}`} />
        </Modal>
      )}
    </section>
  );
}

function Card({
  n,
  title,
  state,
  wide,
  children,
}: {
  n: number;
  title: string;
  state: StepState;
  wide?: boolean;
  children: ReactNode;
}) {
  return (
    <section
      id={`step-${n}`}
      className={`desk-card desk-step is-${state}${wide ? ' is-wide' : ''}`}
    >
      <h2 className="desk-card-title">
        <span className="desk-step-number">{n}</span>
        {title}
        {state === 'done' && <CheckCircleFilled className="desk-step-icon" />}
        {state === 'warn' && (
          <ExclamationCircleFilled className="desk-step-icon" />
        )}
      </h2>
      {children}
    </section>
  );
}

function BillsTable({
  rows,
  deletable,
  onOpen,
}: {
  rows: MonthBillRow[];
  deletable: Set<string>;
  onOpen: (id: string) => void;
}) {
  const { modal, message } = App.useApp();
  const [, remove] = useMutation(DeleteBillDocument);
  const { ref: fitRef, fit } = useFitColumns<HTMLDivElement>();

  async function confirmDelete(b: MonthBillRow) {
    const ok = await modal.confirm({
      title: 'حذف الفاتورة المكررة',
      content: `حذف فاتورة ${b.member?.fullName} (القراءة ${b.currentReading}، ${dirhams(b.billTotal)})؟ لا يمكن التراجع.`,
      okText: 'احذفها',
      okButtonProps: { danger: true },
      cancelText: 'رجوع',
    });
    if (!ok) return;
    const r = await remove({ id: b.id }, closeContext);
    if (r.error) message.error(describeError(r.error, 'تعذّر حذف الفاتورة.'));
    else message.success('حُذفت الفاتورة المكررة');
  }

  return (
    <div ref={fitRef}>
      <Table<MonthBillRow>
        rowKey="id"
        size="small"
        tableLayout="fixed"
        dataSource={rows}
        pagination={
          rows.length > 100 ? { pageSize: 100, size: 'small' } : false
        }
        rowClassName={() => 'is-clickable'}
        onRow={(b) => ({ onClick: () => onOpen(b.id) })}
        locale={{ emptyText: 'لا فواتير' }}
        columns={fit<MonthBillRow>([
          {
            key: 'member',
            title: 'العضو',
            ellipsis: true,
            render: (_, b) => b.member?.fullName,
          },
          {
            key: 'meter',
            drop: 3,
            title: 'العداد',
            ellipsis: true,
            render: (_, b) => b.meter?.label,
          },
          {
            key: 'reading',
            title: 'القراءة',
            width: 140,
            render: (_, b) => (
              <span className="ltr-num">
                {b.previousReading} → {b.currentReading}
              </span>
            ),
          },
          {
            key: 'consumption',
            drop: 2,
            title: 'الاستهلاك',
            width: 110,
            render: (_, b) => (
              <span
                className={
                  (b.consumption ?? 0) > HIGH_CONSUMPTION
                    ? 'desk-high'
                    : undefined
                }
              >
                {cubicMeters(b.consumption)}
              </span>
            ),
          },
          {
            key: 'photo',
            drop: 1,
            title: 'صورة',
            width: 56,
            render: (_, b) =>
              b.readingPhoto ? (
                <a
                  href={b.readingPhoto.url}
                  target="_blank"
                  rel="noreferrer"
                  aria-label="صورة العداد"
                  onClick={(e) => e.stopPropagation()}
                >
                  <CameraFilled className="receipt-mark" />
                </a>
              ) : (b.consumption ?? 0) > HIGH_CONSUMPTION ? (
                <span className="field-hint">—</span>
              ) : null,
          },
          {
            key: 'total',
            title: 'المبلغ',
            width: 120,
            render: (_, b) => dirhams(b.billTotal),
          },
          {
            key: 'status',
            title: 'الأداء',
            width: 150,
            render: (_, b) => (
              <span className="desk-row-actions">
                {b.billStatus === 'PAID' ? (
                  <Tag color="green">مؤداة</Tag>
                ) : (
                  <Tag color={b.billStatus === 'OVERDUE' ? 'red' : 'orange'}>
                    {b.billStatus === 'OVERDUE' ? 'متأخرة' : 'غير مؤداة'}
                  </Tag>
                )}
                {deletable.has(b.id) && (
                  <Button
                    size="small"
                    danger
                    type="text"
                    onClick={(e) => {
                      e.stopPropagation();
                      confirmDelete(b);
                    }}
                  >
                    حذف
                  </Button>
                )}
              </span>
            ),
          },
        ])}
      />
    </div>
  );
}

type SendResult = {
  queued: number;
  alreadySent: number;
  noWhatsapp: number;
  paid: number;
};

/** Send the month's invoices (a summary and a confirmation first). */
function WhatsappCard({
  month,
  start,
  end,
  unreadCount,
  duplicateCount,
  hasBills,
  canSend,
}: {
  month: MonthKey;
  start: string;
  end: string;
  unreadCount: number;
  duplicateCount: number;
  hasBills: boolean;
  canSend: boolean;
}) {
  const { modal } = App.useApp();
  const [{ data }, refetch] = useQuery({
    query: MonthWhatsappDocument,
    variables: { start, end },
    context: useMemo(() => closeContext, []),
  });
  const [{ fetching: sending }, sendMonth] = useMutation(
    SendMonthInvoicesDocument,
  );
  const [, resend] = useMutation(ResendInvoiceDocument);
  const [sent, setSent] = useState<SendResult | null>(null);
  const [problem, setProblem] = useState<string | null>(null);
  const messages = useMemo(
    () => latestPerBill(data?.whatsappMessages?.nodes ?? []),
    [data],
  );
  const counts = countByStatus(messages);
  const failed = messages.filter((m) => m.status === 'FAILED');

  // The worker sends queued messages one by one: refresh until none is left.
  useEffect(() => {
    if (!counts.QUEUED) return;
    const timer = setInterval(
      () => refetch({ requestPolicy: 'network-only' }),
      5000,
    );
    return () => clearInterval(timer);
  }, [counts.QUEUED, refetch]);

  // biome-ignore lint/correctness/useExhaustiveDependencies: reset on month change
  useEffect(() => {
    setSent(null);
    setProblem(null);
  }, [month]);

  async function send() {
    const warnings = [
      unreadCount
        ? `${metersCount(unreadCount)} بدون قراءة: لن تُرسل فواتيرها الآن.`
        : '',
      duplicateCount
        ? `${metersCount(duplicateCount)} بفاتورتين لنفس الشهر: سيتوصل العضو بالاثنتين (الخطوة 2).`
        : '',
    ].filter(Boolean);
    const ok = await modal.confirm({
      title: `إرسال فواتير ${monthLabel(month)} عبر واتساب`,
      width: 520,
      content: (
        <>
          <p>
            تُرسل الفواتير غير المؤداة للأعضاء الذين لهم رقم واتساب صالح. لا تُعاد
            الفواتير المرسلة من قبل.
          </p>
          {warnings.map((w) => (
            <p key={w} className="field-error">
              {w}
            </p>
          ))}
        </>
      ),
      okText: 'إرسال',
      cancelText: 'رجوع',
    });
    if (!ok) return;
    setProblem(null);
    const r = await sendMonth({ month: start }, closeContext);
    const summary = r.data?.appSendMonthInvoices?.result;
    if (r.error || !summary) {
      setProblem(describeError(r.error, 'تعذّر الإرسال.', WHATSAPP_ERRORS));
      return;
    }
    setSent(summary);
  }

  async function resendOne(billId: string) {
    const r = await resend({ billId }, closeContext);
    if (r.error) {
      setProblem(describeError(r.error, 'تعذّر الإرسال.', WHATSAPP_ERRORS));
    }
  }

  return (
    <>
      {messages.length ? (
        <div className="desk-tags">
          {(Object.keys(counts) as WhatsappStatus[])
            .filter((s) => counts[s])
            .map((s) => (
              <Tag key={s} color={WHATSAPP_STATUS_COLORS[s]}>
                {WHATSAPP_STATUS_LABELS[s]}: {counts[s]}
              </Tag>
            ))}
        </div>
      ) : (
        <div className="field-hint">لم تُرسل أي فاتورة لهذا الشهر بعد.</div>
      )}
      {canSend && (
        <Button
          type="primary"
          icon={<WhatsAppOutlined />}
          loading={sending}
          disabled={!hasBills}
          onClick={send}
        >
          {messages.length ? 'إرسال الفواتير الجديدة' : 'إرسال فواتير الشهر'}
        </Button>
      )}
      {sent && (
        <Alert
          type={sent.queued ? 'success' : 'info'}
          showIcon
          message={
            sent.queued
              ? `${billsCount(sent.queued)} في طريقها إلى الأعضاء`
              : 'لا توجد فواتير جديدة للإرسال'
          }
          description={[
            sent.alreadySent ? `أُرسلت من قبل: ${sent.alreadySent}` : '',
            sent.noWhatsapp
              ? `بدون واتساب: ${sent.noWhatsapp} (تُطبع لهم، الخطوة 4)`
              : '',
            sent.paid ? `مؤداة: ${sent.paid}` : '',
          ]
            .filter(Boolean)
            .join(' · ')}
        />
      )}
      {problem && <Alert type="error" showIcon message={problem} />}
      {failed.length > 0 && (
        <>
          <div className="field-label">رسائل فشلت ({failed.length})</div>
          {failed.map((m) => (
            <div key={m.id} className="desk-line">
              <span>
                <strong>{m.member?.fullName}</strong>{' '}
                <span className="ltr-num field-hint">{m.toPhone}</span>
                {m.error && <div className="field-hint">{m.error}</div>}
              </span>
              {canSend && m.billId && (
                <Button
                  size="small"
                  onClick={() => m.billId && resendOne(m.billId)}
                >
                  إعادة
                </Button>
              )}
            </div>
          ))}
        </>
      )}
    </>
  );
}

/** The bulletin and the late-payers images, to share in the groups. */
function SharingCard({
  month,
  bulletinLink,
}: {
  month: MonthKey;
  bulletinLink: string;
}) {
  const [audience, setAudience] = useState<'PUBLIC' | 'COMMITTEE' | null>(null);
  // Asked for on demand: arqam renders each image when opened.
  const [{ data, fetching, error }] = useQuery({
    query: LatePayersDocument,
    variables: { audience: audience ?? 'PUBLIC' },
    pause: audience === null,
  });
  const images = data?.latePayersImages ?? [];

  return (
    <>
      <div className="desk-link-list">
        <a href={bulletinLink} target="_blank" rel="noreferrer">
          <FilePdfOutlined /> النشرة الشهرية ({monthLabel(month)})
        </a>
      </div>
      <div className="desk-row-actions">
        <Button
          type={audience === 'PUBLIC' ? 'primary' : 'default'}
          onClick={() => setAudience('PUBLIC')}
        >
          المتأخرون: المجموعة العامة
        </Button>
        <Button
          type={audience === 'COMMITTEE' ? 'primary' : 'default'}
          onClick={() => setAudience('COMMITTEE')}
        >
          المتأخرون: مجموعة اللجنة
        </Button>
      </div>
      {fetching && <Skeleton.Button active block />}
      {error && <Alert type="error" message="تعذّر إنشاء الصور" />}
      {audience && !fetching && images.length > 0 && (
        <div className="desk-link-list">
          {images.map((link, i) => (
            <span key={link} className="desk-line">
              <a href={link} target="_blank" rel="noreferrer">
                الصورة {i + 1} من {images.length}
              </a>
              <a href={withDownload(link)}>
                <DownloadOutlined /> تحميل
              </a>
            </span>
          ))}
        </div>
      )}
      <div className="field-hint">
        المشاركة في مجموعة واتساب أسهل من الهاتف: تطبيق اللجنة، «الشهر».
      </div>
    </>
  );
}
